import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, CheckCircle } from 'lucide-react';

// Condition-specific copy
const CONDITION_CONFIG = {
  menopause: {
    eyebrow: 'Free Download',
    title: 'Wait, before you go',
    subtitle: 'Get the Menopause Symptom Checklist used by women who finally got real answers (and real relief).',
    benefits: [
      'The 14 hormonal symptoms most doctors dismiss',
      'What to ask for in your next labs (and why "normal" is not enough)',
      'When HRT is still safe, and the timing window most women miss',
    ],
    submit: 'Send My Free Checklist',
    success: 'Your Menopause Checklist is on its way. Check your inbox in a few minutes.',
    source: 'menopause_exit_intent_checklist',
    storageKey: 'glow_meno_popup_seen',
  },
  perimenopause: {
    eyebrow: 'Free Download',
    title: 'Wait, before you go',
    subtitle: 'Get the Perimenopause Symptom Checklist that helps you spot what is actually happening (years before menopause).',
    benefits: [
      'The 12 early perimenopausal signs most women miss in their 30s and 40s',
      'Which labs to request to confirm what your body is telling you',
      'How to talk to your doctor without being dismissed',
    ],
    submit: 'Send My Free Checklist',
    success: 'Your Perimenopause Checklist is on its way. Check your inbox in a few minutes.',
    source: 'perimenopause_exit_intent_checklist',
    storageKey: 'glow_peri_popup_seen',
  },
  pmos: {
    eyebrow: 'Free Download',
    title: 'Wait, before you go',
    subtitle: 'Get the PMOS (formerly PCOS) Symptom Checklist used by women who finally got the diagnosis and care they deserved.',
    benefits: [
      'The 4 PMOS subtypes most doctors never test for',
      'The labs that catch what a basic panel misses',
      'Why the "just lose weight" advice keeps backfiring (and what works instead)',
    ],
    submit: 'Send My Free Checklist',
    success: 'Your PMOS Checklist is on its way. Check your inbox in a few minutes.',
    source: 'pmos_exit_intent_checklist',
    storageKey: 'glow_pmos_popup_seen',
  },
  pmdd: {
    eyebrow: 'Free Download',
    title: 'Wait, before you go',
    subtitle: 'Get the PMDD Symptom Tracker used by women who finally figured out what was actually happening every cycle.',
    benefits: [
      'The 11 PMDD symptoms that get misdiagnosed as anxiety or depression',
      'A 2-cycle tracking template that gives you real diagnostic data',
      'How to talk to your provider so they actually take you seriously',
    ],
    submit: 'Send My Free Tracker',
    success: 'Your PMDD Tracker is on its way. Check your inbox in a few minutes.',
    source: 'pmdd_exit_intent_tracker',
    storageKey: 'glow_pmdd_popup_seen',
  },
  postpartum: {
    eyebrow: 'Free Download',
    title: 'Wait, before you go',
    subtitle: 'Get the Postpartum Hormone & Mood Checklist used by mothers who finally got real care after the 6-week brush-off.',
    benefits: [
      'The 9 postpartum symptoms most often dismissed as "just being a mom"',
      'The exact labs to ask for (and why thyroid alone is not enough)',
      'When to seek help, and what to say so you actually get listened to',
    ],
    submit: 'Send My Free Checklist',
    success: 'Your Postpartum Checklist is on its way. Check your inbox in a few minutes.',
    source: 'postpartum_exit_intent_checklist',
    storageKey: 'glow_postpartum_popup_seen',
  },
  thyroid: {
    eyebrow: 'Free Download',
    title: 'Wait, before you go',
    subtitle: 'Get the Thyroid Symptom & Lab Guide used by women who finally got the full panel, and the answers, they had been asking for.',
    benefits: [
      'The 12 thyroid symptoms most providers never connect to your thyroid',
      'The exact lab panel to ask for (TSH alone is not enough)',
      'How to read your results, what is "normal" vs. what is actually optimal',
    ],
    submit: 'Send My Free Guide',
    success: 'Your Thyroid Guide is on its way. Check your inbox in a few minutes.',
    source: 'thyroid_exit_intent_guide',
    storageKey: 'glow_thyroid_popup_seen',
  },
};

const COOLDOWN_DAYS = 14;
const MIN_TIME_ON_PAGE_MS = 15000; // 15s before trigger eligible

const ConditionExitPopup = ({ condition }) => {
  const config = CONDITION_CONFIG[condition];
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const hasTriggered = useRef(false);
  const pageEnteredAt = useRef(Date.now());
  const lastScrollY = useRef(0);

  const isInCooldown = useCallback(() => {
    if (!config) return true;
    const lastShown = localStorage.getItem(config.storageKey);
    if (!lastShown) return false;
    const days = (Date.now() - parseInt(lastShown, 10)) / (1000 * 60 * 60 * 24);
    return days < COOLDOWN_DAYS;
  }, [config]);

  const trigger = useCallback(() => {
    if (hasTriggered.current) return;
    if (Date.now() - pageEnteredAt.current < MIN_TIME_ON_PAGE_MS) return;
    if (isInCooldown()) return;
    hasTriggered.current = true;
    setIsVisible(true);
  }, [isInCooldown]);

  useEffect(() => {
    if (!config) return;
    pageEnteredAt.current = Date.now();
    hasTriggered.current = false;

    // Desktop: exit intent (mouse leaves top of viewport)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) trigger();
    };

    // Mobile: rapid upward scroll near top of page after engagement
    const handleScroll = () => {
      const y = window.scrollY;
      // upward scroll of 50+ px while in upper portion of page = "leaving" intent
      if (lastScrollY.current - y > 50 && y < 200) {
        trigger();
      }
      lastScrollY.current = y;
    };

    // Fallback: if user is idle 60s, show it once
    const idleTimer = setTimeout(() => trigger(), 60000);

    document.addEventListener('mouseout', handleMouseLeave);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mouseout', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(idleTimer);
    };
  }, [config, trigger]);

  const handleClose = () => {
    setIsVisible(false);
    if (config) {
      localStorage.setItem(config.storageKey, Date.now().toString());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/leads/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: firstName || null,
          source: config.source,
          pageUrl: window.location.href,
        }),
      });
      if (!response.ok) throw new Error('Failed to submit');
      setIsSuccess(true);
      localStorage.setItem(config.storageKey, Date.now().toString());
      setTimeout(() => setIsVisible(false), 4500);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!config || !isVisible) return null;

  return (
    <div
      className="popup-overlay"
      onClick={handleClose}
      data-testid={`condition-popup-overlay-${condition}`}
    >
      <div
        className="popup-container popup-low-intent popup-condition"
        onClick={(e) => e.stopPropagation()}
        data-testid={`condition-popup-${condition}`}
      >
        <button
          className="popup-close"
          onClick={handleClose}
          aria-label="Close"
          data-testid={`condition-popup-close-${condition}`}
        >
          <X size={18} />
        </button>

        {!isSuccess ? (
          <>
            <div className="popup-header">
              <span className="popup-eyebrow">{config.eyebrow}</span>
              <h2 className="popup-title">{config.title}</h2>
              <p className="popup-subtitle">{config.subtitle}</p>
            </div>

            <ul className="popup-benefit-list">
              {config.benefits.map((b, i) => (
                <li key={i}>
                  <CheckCircle size={16} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <form onSubmit={handleSubmit} className="popup-form">
              <div className="popup-field">
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="popup-input"
                  data-testid={`condition-popup-firstname-${condition}`}
                />
              </div>
              <div className="popup-field">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="popup-input"
                  required
                  data-testid={`condition-popup-email-${condition}`}
                />
              </div>

              {error && <p className="popup-error">{error}</p>}

              <button
                type="submit"
                className="popup-submit"
                disabled={isSubmitting}
                data-testid={`condition-popup-submit-${condition}`}
              >
                {isSubmitting ? 'Sending...' : config.submit}
              </button>
            </form>

            <p className="popup-privacy">
              No spam. Unsubscribe anytime.
            </p>
          </>
        ) : (
          <div className="popup-success">
            <div className="popup-success-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="28" cy="28" r="26" stroke="#CBB26A" strokeWidth="2" />
                <path d="M18 28L25 35L38 22" stroke="#CBB26A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="popup-success-title">Check your inbox</h2>
            <p className="popup-success-text">{config.success}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConditionExitPopup;
